import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CalendarDays, DollarSign, Users, Inbox, TrendingUp, Beer } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="paper-card p-6 hover:-translate-y-0.5 transition-transform">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#c97b3a] font-semibold">{label}</div>
        <div className="font-display text-4xl text-[#1f2e1f] mt-2">{value}</div>
        {sub && <div className="text-xs text-[#8c8577] mt-1">{sub}</div>}
      </div>
      <div className="h-10 w-10 rounded-full bg-[#1f2e1f] flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#c97b3a]" />
      </div>
    </div>
  </div>
);

export default function StatsOverview({ refreshKey }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [refreshKey]);

  if (!stats) return <div className="text-[#8c8577]">Loading stats…</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="stat-revenue" icon={DollarSign} label="Total Revenue" value={`$${stats.total_revenue.toLocaleString()}`} sub="all-time (non-cancelled)" />
        <KPI testid="stat-bookings" icon={Beer} label="Bookings" value={stats.total_bookings} sub={`${stats.upcoming_events} upcoming`} />
        <KPI testid="stat-inquiries" icon={Inbox} label="New Inquiries" value={stats.new_inquiries} sub="awaiting your reply" />
        <KPI testid="stat-customers" icon={Users} label="Customers" value={stats.total_customers} sub={`${stats.conversion_rate}% conversion`} />
      </div>

      <div className="paper-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#c97b3a] font-semibold">Monthly Revenue</div>
            <h3 className="font-display text-2xl text-[#1f2e1f] mt-1">Pouring trends</h3>
          </div>
          <TrendingUp className="h-5 w-5 text-[#c97b3a]" />
        </div>
        {stats.monthly_revenue.length === 0 ? (
          <div className="text-[#8c8577] text-sm py-12 text-center">No revenue data yet</div>
        ) : (
          <div className="h-80 w-full" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly_revenue} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#dcd3c6" />
                <XAxis dataKey="month" stroke="#5a5245" fontSize={12} />
                <YAxis stroke="#5a5245" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1f2e1f", border: "none", borderRadius: "8px", color: "#F8F5F0" }}
                  formatter={(v) => [`$${v}`, "Revenue"]}
                />
                <Bar dataKey="revenue" fill="#c97b3a" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="paper-card p-6 flex items-center gap-4">
        <CalendarDays className="h-6 w-6 text-[#c97b3a]" />
        <div className="text-sm text-[#5a5245]">
          Looking for what's next? Head to the <span className="font-semibold text-[#1f2e1f]">Bookings</span> tab to see your event calendar.
        </div>
      </div>
    </div>
  );
}
